import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogBody, DialogTitle } from './dialog';
import { Button } from './button';
import { restatearRepartoEnLotes } from '../api/comisiones';
import { candidatosDesde } from '../utils/reparto';

const fmtMXN = (n) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(n) || 0);

const fmtFecha = (ms) =>
  ms ? new Date(Number(ms)).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const field =
  'rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm outline-none ' +
  'focus:border-azul-surelia focus:ring-1 focus:ring-azul-surelia';

/**
 * RepartoRetroactivoDialog — after a reparto change, lists the fianzas already
 * on the books whose booked cut differs from the new one and lets the owner
 * tick which to restate. Pendientes and cobradas alike: the reparto is a cut
 * of comisionMonto, never a change to it, so nothing about the money moves.
 *
 * `candidatos` come from utils/reparto candidatosReparto, newest emisión
 * first. Every row shows its fecha de emisión; a "Desde" date narrows the
 * list (the asociado joined from some month on), and the header checkbox
 * selects or clears every visible row at once.
 *
 * Closing is a decision, never an escape: the X, Escape and the backdrop all
 * ask once before leaving the earlier fianzas as they are. The desfase stays
 * visible afterwards (ClienteAsociados shows "N fianzas con reparto anterior"
 * and reopens this dialog), so nothing is lost by closing.
 *
 * With `onEnviar` (org requires approval) nothing is restated here: the ticked
 * fianzas travel with the propuesta and are restated once the socios approve.
 */
export function RepartoRetroactivoDialog({ isOpen, onClose, candidatos, motivo, onEnviar }) {
  const [selected, setSelected] = useState(new Set());
  const [desde, setDesde] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [progreso, setProgreso] = useState(null);
  const [confirmarCierre, setConfirmarCierre] = useState(false);
  const headerRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setSelected(new Set((candidatos || []).map((c) => c.fianzaId)));
      setDesde('');
      setError(null);
      setProgreso(null);
      setConfirmarCierre(false);
    }
  }, [isOpen, candidatos]);

  const visibles = useMemo(() => candidatosDesde(candidatos, desde), [candidatos, desde]);
  const ocultas = (candidatos || []).length - visibles.length;
  const elegidas = visibles.filter((c) => selected.has(c.fianzaId));
  const todas = visibles.length > 0 && elegidas.length === visibles.length;
  const algunas = elegidas.length > 0 && !todas;

  useEffect(() => {
    if (headerRef.current) headerRef.current.indeterminate = algunas;
  }, [algunas]);

  const toggle = (fianzaId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(fianzaId)) next.delete(fianzaId);
      else next.add(fianzaId);
      return next;
    });
  };

  const toggleTodas = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (todas) visibles.forEach((c) => next.delete(c.fianzaId));
      else visibles.forEach((c) => next.add(c.fianzaId));
      return next;
    });
  };

  // X, Escape, backdrop and the "dejar como están" button all land here.
  const pedirCierre = () => {
    if (busy) return;
    if (confirmarCierre) return;
    setConfirmarCierre(true);
  };

  const dejarComoEstan = async () => {
    if (onEnviar) {
      await enviar([]);
      return;
    }
    onClose();
  };

  const enviar = async (cambios) => {
    setBusy(true);
    setError(null);
    try {
      await onEnviar(cambios);
      onClose();
    } catch (e) {
      setError(e.message || 'No se pudo enviar la propuesta.');
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    const cambios = elegidas.map((c) => ({ contractId: c.contractId, fianzaId: c.fianzaId }));
    if (!cambios.length) return;
    if (onEnviar) {
      await enviar(cambios);
      return;
    }
    setBusy(true);
    setError(null);
    setProgreso({ hechas: 0, total: cambios.length });
    try {
      const r = await restatearRepartoEnLotes({ cambios, motivo }, (hechas, total) =>
        setProgreso({ hechas, total })
      );
      if (r.errores?.length) {
        setError(`Se ajustaron ${r.actualizadas} de ${cambios.length} fianzas; ` +
          `${r.errores.length} no se pudieron ajustar.`);
        return;
      }
      onClose();
    } catch (e) {
      console.error('reparto restatement failed', e);
      const hechas = e.parcial?.actualizadas || 0;
      setError(
        hechas
          ? `${e.message || 'No se pudo ajustar el reparto.'} Se ajustaron ${hechas} de ` +
            `${cambios.length} fianzas; vuelve a confirmar para aplicar las restantes.`
          : e.message || 'No se pudo ajustar el reparto.'
      );
    } finally {
      setBusy(false);
      setProgreso(null);
    }
  };

  const total = (candidatos || []).length;

  return (
    <Dialog open={isOpen} onClose={pedirCierre} size="3xl">
      <DialogTitle>Ajustar fianzas anteriores</DialogTitle>
      <DialogBody>
        {error && (
          <div className="mb-3 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">{error}</div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div className="text-xs text-gray-500">
            {total} fianza{total !== 1 ? 's' : ''} con reparto distinto al vigente
            {ocultas > 0 && <span className="text-gray-400"> · {ocultas} antes de la fecha</span>}
          </div>
          <label className="flex items-center gap-2 text-xs text-gray-600">
            <span>Desde</span>
            <input
              type="date"
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
              className={field}
              disabled={busy}
            />
            {desde && (
              <button type="button" className="text-gray-400 hover:text-gray-600" onClick={() => setDesde('')}>
                Quitar
              </button>
            )}
          </label>
        </div>

        <div className="border border-gray-200 rounded-lg overflow-x-auto max-h-[28rem] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white">
              <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                <th className="py-2 px-3 w-8">
                  <input
                    ref={headerRef}
                    type="checkbox"
                    checked={todas}
                    onChange={toggleTodas}
                    disabled={busy || visibles.length === 0}
                    title={todas ? 'Quitar selección' : 'Seleccionar todas'}
                    className="w-4 h-4 text-azul-surelia border-gray-300 rounded"
                  />
                </th>
                <th className="py-2 px-3 whitespace-nowrap">Emisión</th>
                <th className="py-2 px-3">Fianza</th>
                <th className="py-2 px-3 text-right">Comisión</th>
                <th className="py-2 px-3">Reparto</th>
              </tr>
            </thead>
            <tbody>
              {visibles.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-6 px-3 text-center text-sm text-gray-400">
                    Ninguna fianza a partir de esa fecha.
                  </td>
                </tr>
              )}
              {visibles.map((c) => (
                <tr
                  key={c.fianzaId}
                  className="border-b border-gray-100 last:border-0 cursor-pointer hover:bg-zinc-50 align-top"
                  onClick={() => !busy && toggle(c.fianzaId)}
                >
                  <td className="py-2 px-3">
                    <input
                      type="checkbox"
                      checked={selected.has(c.fianzaId)}
                      onChange={() => toggle(c.fianzaId)}
                      onClick={(e) => e.stopPropagation()}
                      disabled={busy}
                      className="w-4 h-4 text-azul-surelia border-gray-300 rounded"
                    />
                  </td>
                  <td className="py-2 px-3 whitespace-nowrap tabular-nums text-gray-600">{fmtFecha(c.fecha)}</td>
                  <td className="py-2 px-3">
                    <div className="font-medium text-gray-800">{c.numeroFianza || 'Sin número'}</div>
                    {(c.fiadoNombre || c.afianzadora) && (
                      <div className="text-xs text-gray-500 truncate max-w-[18rem]">
                        {[c.fiadoNombre, c.afianzadora].filter(Boolean).join(' · ')}
                      </div>
                    )}
                    {c.clienteNombre && <div className="text-xs text-gray-400">{c.clienteNombre}</div>}
                  </td>
                  <td className="py-2 px-3 text-right tabular-nums text-gray-700">{fmtMXN(c.comision)}</td>
                  <td className="py-2 px-3">
                    <div className="space-y-0.5">
                      {c.diferencias.map((d) => (
                        <div key={d.asociadoId} className="flex justify-between gap-4 text-xs tabular-nums">
                          <span className="text-gray-600 truncate">{d.nombre}</span>
                          <span className="whitespace-nowrap">
                            <span className="text-gray-400">{fmtMXN(d.antes)}</span>
                            <span className="mx-1 text-gray-300">a</span>
                            <span className="font-semibold text-gray-800">{fmtMXN(d.despues)}</span>
                          </span>
                        </div>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {confirmarCierre ? (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-amber-900">
              {onEnviar
                ? 'La propuesta se envía solo a partir de hoy. Las fianzas anteriores quedan con el reparto anterior.'
                : 'Las fianzas anteriores quedan con el reparto anterior. Puedes ajustarlas después desde la pestaña Asociados del cliente.'}
            </p>
            <div className="flex gap-2">
              <Button plain onClick={() => setConfirmarCierre(false)} disabled={busy}>
                Volver
              </Button>
              <Button color="surelia" onClick={dejarComoEstan} disabled={busy}>
                {busy ? 'Enviando...' : 'Dejar como están'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex justify-end gap-2 mt-4">
            <Button plain onClick={pedirCierre} disabled={busy}>
              Dejar las anteriores como están
            </Button>
            <Button color="surelia" onClick={confirm} disabled={busy || elegidas.length === 0}>
              {busy
                ? progreso
                  ? `Ajustando ${progreso.hechas} de ${progreso.total}...`
                  : onEnviar ? 'Enviando...' : 'Ajustando...'
                : onEnviar
                  ? `Enviar a aprobación con ${elegidas.length} de ${visibles.length}`
                  : `Ajustar ${elegidas.length} de ${visibles.length}`}
            </Button>
          </div>
        )}
      </DialogBody>
    </Dialog>
  );
}

export default RepartoRetroactivoDialog;
